import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { haptic } from '../../hooks/useTelegram';
import { useSession } from '../../hooks/useSession';
import { useActiveWallet } from '../../store/wallets';
import { copyText } from '../../lib/clipboard';
import { fmtUsd, shortAddr, fmtDateDMY } from '../../lib/format';
import { rpcErrorCode } from '../../lib/api';
import {
  fetchReferral,
  fetchReferralEarnings,
  fetchReferralPayouts,
  withdrawReferral,
  type ReferralEarning,
} from '../../lib/referral';
import { useToast } from '../../store/toast';
import { FigIcon } from '../FigIcon';
import { ReauthGate } from './ReauthGate';

/** Each revenue source gets its own label — "you earned $0.02" says nothing
 *  about which friend did what. */
const SOURCE_KEYS: Record<string, string> = {
  transfer: 'referral.srcTransfer',
  card_issue: 'referral.srcCardIssue',
  card_topup: 'referral.srcCardTopup',
  qr: 'referral.srcQr',
  onramp: 'referral.srcOnramp',
  offramp: 'referral.srcOfframp',
};

const PAYOUT_KEYS: Record<string, string> = {
  requested: 'referral.requested',
  sending: 'referral.sending',
  paid: 'referral.sent',
  failed: 'referral.failed',
};

function errorKey(err: unknown): string {
  const code = rpcErrorCode(err);
  if (code === 'below_minimum') return 'referral.errBelowMin';
  if (code === 'withdrawal_in_progress') return 'referral.errInProgress';
  return 'referral.errFailed';
}

/** Open Telegram's own share sheet with the invite link — the one place a user
 *  can forward it to a chat without leaving the app. */
function shareLink(link: string, text: string) {
  const url = `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`;
  const wa = window.Telegram?.WebApp;
  if (wa?.openTelegramLink) wa.openTelegramLink(url);
  else window.open(url, '_blank');
}

export function ReferralScreen() {
  const { t } = useTranslation();
  const { token } = useSession();
  const wallet = useActiveWallet();
  const qc = useQueryClient();
  const showToast = useToast((s) => s.show);
  const [gate, setGate] = useState(false);

  const summary = useQuery({
    queryKey: ['referral', 'summary'],
    enabled: !!token,
    queryFn: fetchReferral,
  });
  const earnings = useQuery({
    queryKey: ['referral', 'earnings'],
    enabled: !!token,
    queryFn: fetchReferralEarnings,
  });
  const payouts = useQuery({
    queryKey: ['referral', 'payouts'],
    enabled: !!token,
    queryFn: fetchReferralPayouts,
  });

  const s = summary.data;

  const runWithdraw = async (reauthToken?: string): Promise<boolean> => {
    if (!wallet) return false;
    try {
      await withdrawReferral(wallet.id, reauthToken);
    } catch (err) {
      haptic('medium');
      showToast(t(errorKey(err)));
      return false;
    }
    haptic();
    setGate(false);
    showToast(t('referral.queued'));
    await qc.invalidateQueries({ queryKey: ['referral'] });
    return true;
  };

  if (gate) {
    return (
      <ReauthGate
        onAuthed={runWithdraw}
        bioTitle={t('referral.confirmTitle')}
        bioHint={t('referral.unlockHint')}
      />
    );
  }

  return (
    <div className="push-enter flex min-h-full flex-col bg-surface px-4 pb-16 pt-3">
      <header className="flex items-center py-2">
        <h1 className="text-[20px] font-medium text-ink">{t('referral.title')}</h1>
      </header>

      {summary.isLoading ? (
        <div className="flex flex-1 items-center justify-center py-20">
          <span className="size-10 animate-spin rounded-full border-[3px] border-border border-t-accent" />
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="mb-1 mt-1 text-[13px] leading-[1.44] text-muted">{t('referral.hint')}</p>

          {s && !s.enabled && (
            <p className="rounded-card bg-card p-4 text-[13px] leading-[1.44] text-muted shadow-card">
              {t('referral.disabled')}
            </p>
          )}

          {/* The link itself, first — it is what the screen is for. */}
          <div className="flex flex-col gap-3 rounded-card bg-card p-4 shadow-card">
            <p className="text-[13px] text-muted">{t('referral.yourLink')}</p>
            <p className="break-all text-[14px] font-medium text-ink">{s?.link ?? ''}</p>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  if (s?.link) void copyText(s.link);
                }}
                className="flex-1 rounded-[12px] bg-accent py-3 text-[14px] font-semibold text-white active:opacity-80"
              >
                {t('referral.copy')}
              </button>
              <button
                onClick={() => {
                  haptic();
                  if (s?.link) shareLink(s.link, t('referral.hint'));
                }}
                className="flex-1 rounded-[12px] bg-surface py-3 text-[14px] font-semibold text-ink active:opacity-70"
              >
                {t('referral.share')}
              </button>
            </div>
          </div>

          {/* What it has produced. */}
          <div className="grid grid-cols-3 gap-2">
            <Stat label={t('referral.invited')} value={String(s?.invited ?? 0)} />
            <Stat label={t('referral.earned')} value={fmtUsd(s?.earnedUsd ?? 0)} />
            <Stat label={t('referral.paid')} value={fmtUsd(s?.paidUsd ?? 0)} />
          </div>

          <div className="flex flex-col gap-3 rounded-card bg-card p-4 shadow-card">
            <div className="flex items-end justify-between gap-3">
              <div className="flex flex-col gap-1">
                <p className="text-[13px] text-muted">{t('referral.balance')}</p>
                <p className="text-[24px] font-medium text-ink">{fmtUsd(s?.balanceUsd ?? 0)}</p>
              </div>
              <button
                disabled={!s?.canWithdraw || !wallet}
                onClick={() => {
                  haptic();
                  setGate(true);
                }}
                className="rounded-[12px] bg-accent px-5 py-3 text-[14px] font-semibold text-white active:opacity-80 disabled:opacity-40"
              >
                {t('referral.withdraw')}
              </button>
            </div>
            {/* Either how far there is to go, or where the money will land —
             *  never both, and never a bare disabled button with no reason. */}
            {s && !s.canWithdraw ? (
              <p className="text-[12px] text-muted">
                {t('referral.minHint', { amount: fmtUsd(s.minWithdrawUsd) })}
                {s.balanceUsd > 0 && (
                  <>
                    {' · '}
                    {t('referral.toGo', {
                      amount: fmtUsd(Math.max(0, s.minWithdrawUsd - s.balanceUsd)),
                    })}
                  </>
                )}
              </p>
            ) : (
              wallet && (
                <p className="text-[12px] text-muted">
                  {t('referral.withdrawTo', { wallet: shortAddr(wallet.address) })}
                </p>
              )
            )}
          </div>

          {payouts.data && payouts.data.length > 0 && (
            <>
              <h2 className="mt-2 px-1 text-[13px] text-muted">{t('referral.payouts')}</h2>
              <div className="flex flex-col rounded-card bg-card px-4 shadow-card">
                {payouts.data.map((p, i) => (
                  <div
                    key={p.id}
                    className={`flex items-center justify-between gap-3 py-3 ${i > 0 ? 'border-t border-hairline' : ''}`}
                  >
                    <div className="flex min-w-0 flex-col">
                      <p className="text-[14px] font-medium text-ink">{fmtUsd(p.amountUsd)}</p>
                      <p className="text-[12px] text-muted">{fmtDateDMY(p.createdAt)}</p>
                    </div>
                    <span
                      className={`text-[13px] font-medium ${p.status === 'failed' ? 'text-negative' : p.status === 'paid' ? 'text-positive' : 'text-muted'}`}
                    >
                      {t(PAYOUT_KEYS[p.status] ?? 'referral.requested')}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          <h2 className="mt-2 px-1 text-[13px] text-muted">{t('referral.earnings')}</h2>
          {earnings.data && earnings.data.length > 0 ? (
            <div className="flex flex-col rounded-card bg-card px-4 shadow-card">
              {earnings.data.map((e, i) => (
                <EarningRow key={e.id} earning={e} first={i === 0} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 rounded-card bg-card px-6 py-10 text-center shadow-card">
              <FigIcon name="pfReceipt" size={28} className="text-muted" />
              <p className="text-[15px] font-medium text-ink">{t('referral.noEarningsTitle')}</p>
              <p className="text-[13px] leading-[1.44] text-muted">{t('referral.noEarnings')}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1 rounded-card bg-card p-3 shadow-card">
      <p className="text-[12px] text-muted">{label}</p>
      <p className="truncate text-[16px] font-medium text-ink">{value}</p>
    </div>
  );
}

function EarningRow({ earning, first }: { earning: ReferralEarning; first: boolean }) {
  const { t } = useTranslation();
  return (
    <div
      className={`flex items-center justify-between gap-3 py-3 ${first ? '' : 'border-t border-hairline'}`}
    >
      <div className="flex min-w-0 flex-col">
        <p className="truncate text-[14px] font-medium text-ink">
          {t(SOURCE_KEYS[earning.source] ?? 'referral.srcTransfer')}
        </p>
        <p className="text-[12px] text-muted">{fmtDateDMY(earning.createdAt)}</p>
      </div>
      <p className={`text-[14px] font-medium ${earning.paid ? 'text-muted' : 'text-positive'}`}>
        +{fmtUsd(earning.amountUsd)}
      </p>
    </div>
  );
}
